// @ts-nocheck
import { useCallback, useState } from 'react';
import useModelInventory from './ModelInventory/useModelInventory';
import {
  useDocumentMarkdownQuery,
  useVersionDetailsQuery,
  useUpdateDocumentMutation,
  useSaveAsNewVersionMutation,
} from '../features/document/api/documentInventoryQueries';
import swalToast from '../components/Common/swalToast';
import { isValueEmpty } from '../utils/Utils';

/**
 * Fetches and previews a document-attribute's document, falling back to the
 * modal's own download action when the file type can't be previewed inline.
 * Shared by every place that lets a user open a document attribute's file
 * without navigating away (view mode, draft comparisons, edit mode).
 */
const useAttributeDocumentPreview = ({ entityId, entityType }) => {
  const [openPreview, setOpenPreview] = useState(false);
  const [selectedDocumentForEdit, setSelectedDocumentForEdit] = useState({});
  const { fetchDocumentDetails } = useModelInventory();
  const { execute: getDocumentMarkdown } = useDocumentMarkdownQuery({
    options: {
      enabled: false,
    },
  });

  const { execute: getVersionDetails } = useVersionDetailsQuery({
    options: {
      enabled: false,
    },
  });

  const { mutateAsync: updateDocument } = useUpdateDocumentMutation();

  const { mutateAsync: saveAsNewVersion } = useSaveAsNewVersionMutation();

  const previewDocument = useCallback(
    async (documentId) => {
      try {
        // fetchDocumentDetails fetches by exact id; getAttributeDocument's
        // `text` param is a free-text search and won't reliably match on id.
        const res = await fetchDocumentDetails({
          entity_type: entityType,
          entity_id: entityId,
          document_id: documentId,
        });
        const doc = res?.data?.[0];
        if (isValueEmpty(doc) || isValueEmpty(doc?.file_type)) {
          swalToast({
            icon: 'error',
            title: 'Document not found or it is deleted.',
          });
          return;
        }
        setSelectedDocumentForEdit(doc);
        setOpenPreview(true);
      } catch (e) {
        swalToast({
          icon: 'error',
          title: `Error occurred while fetching the document. ${e}`,
        });
      }
    },
    [fetchDocumentDetails, entityType, entityId]
  );

  const closePreview = useCallback(() => {
    setOpenPreview(false);
  }, []);

  return {
    previewDocument,
    previewModalProps: {
      open: openPreview,
      handleClose: closePreview,
      selectedDocumentForEdit,
      setSelectedDocumentForEdit,
      saveAsNewVersion,
      updateDocument,
      getDocumentMarkdown,
      getVersionDetails,
    },
  };
};

export default useAttributeDocumentPreview;
